import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useAdditionalFields } from "../hooks/addition-fields";
import { useGetApartments } from "../queries/get-apartments";
import { useGetOrganizations } from "../queries/organization";
import { useGetPersons } from "../queries/person";
import { formatApartmentLabel } from "../services/apartment";
import { formatOrganizationLabel } from "../services/organization";
import { formatPersonLabel } from "../services/person";
import { useLocale } from "../stores/locale";
import { AdditionalFields } from "./additional-fields";
import { Button } from "./button";
import { Input } from "./input";
import { ResidencyNameInput } from "./residency-name-input";
import { Select } from "./select";

export type LeaseFormValues = {
  apartmentId: string;
  fromDate: string;
  toDate: string;
  tenants: (
    | {
        personId: string;
      }
    | {
        organizationId: string;
      }
  )[];
  useCustomName: boolean;
  customName: string;
  additionalFields?: Record<string, string>;
};

export function LeaseForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: LeaseFormValues) => void;
  onSubmit: () => void;
  values: LeaseFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: additionalFields } = useAdditionalFields("lease");
  const { data: apartments } = useGetApartments();

  const isValid = useMemo(() => {
    return (
      values.apartmentId !== "" &&
      values.fromDate.trim() !== "" &&
      values.tenants.length > 0 &&
      values.tenants.every(
        (tenant) =>
          ("personId" in tenant && tenant.personId !== "") ||
          ("organizationId" in tenant && tenant.organizationId !== ""),
      ) &&
      additionalFields.every(
        (field) =>
          !field.required ||
          values.additionalFields?.[field.name]?.trim() !== "",
      )
    );
  }, [values, additionalFields]);
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Select
          className="col-span-2"
          label={t.apartment.apartment}
          options={
            apartments?.map((apartment) => ({
              label: formatApartmentLabel(apartment),
              value: apartment.id,
            })) ?? []
          }
          placeholder={t.select.placeholder}
          value={values.apartmentId}
          onChange={(value) => onChange({ ...values, apartmentId: value })}
        />
        <Input
          required
          disabled={isDisabled}
          label={t.lease.fromDate}
          type="date"
          value={values.fromDate}
          onChange={(e) => onChange({ ...values, fromDate: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.lease.toDate}
          type="date"
          value={values.toDate}
          onChange={(e) => onChange({ ...values, toDate: e.target.value })}
        />
        <div className="col-span-2 gap-2">
          {values.tenants.map((tenant, index) => (
            <TenantSelect
              key={index}
              excludeTenants={values.tenants
                .filter((_, i) => i !== index)
                .map((o) =>
                  "personId" in o
                    ? { personId: o.personId }
                    : { organizationId: o.organizationId },
                )}
              isRemoveDisabled={values.tenants.length === 1}
              tenant={tenant}
              onChange={(tenant) =>
                onChange({
                  ...values,
                  tenants: values.tenants.map((o, i) =>
                    i === index ? tenant : o,
                  ),
                })
              }
              onPrimaryChange={(changes) =>
                // Make sure only one tenant is primary
                onChange({
                  ...values,
                  tenants: values.tenants.map((o) => ({
                    ...o,
                    primary:
                      changes.primary &&
                      (("personId" in changes &&
                        "personId" in o &&
                        changes.personId === o.personId) ||
                        ("organizationId" in changes &&
                          "organizationId" in o &&
                          changes.organizationId === o.organizationId)),
                  })),
                })
              }
              onRemove={() =>
                onChange({
                  ...values,
                  tenants: values.tenants.filter((_, i) => i !== index),
                })
              }
            />
          ))}
        </div>
        <div className="flex col-span-2 gap-2">
          <Button
            onClick={() =>
              onChange({
                ...values,
                tenants: [
                  ...values.tenants,
                  {
                    personId: "",
                  },
                ],
              })
            }
          >
            Add tenant
          </Button>
        </div>
        <ResidencyNameInput
          className="col-span-2"
          customName={values.customName}
          disabled={isDisabled}
          residents={values.tenants}
          useCustomName={values.useCustomName}
          onChange={(changes) => onChange({ ...values, ...changes })}
        />
        <AdditionalFields
          additionalFields={additionalFields}
          disabled={isDisabled}
          values={values.additionalFields ?? {}}
          onChange={(additionalFieldValues) =>
            onChange({
              ...values,
              additionalFields: {
                ...additionalFieldValues,
              },
            })
          }
        />
      </div>
      <div className="flex gap-2 justify-end">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.lease.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}

function TenantSelect({
  tenant,
  excludeTenants = [],
  onChange,
  onRemove,
  onPrimaryChange: _onPrimaryChange,
  isRemoveDisabled = false,
}: {
  tenant: LeaseFormValues["tenants"][0];
  excludeTenants?: ({ personId: string } | { organizationId: string })[];
  onChange: (tenant: LeaseFormValues["tenants"][0]) => void;
  onRemove: () => void;
  onPrimaryChange: (
    changes: ({ personId: string } | { organizationId: string }) & {
      primary: boolean;
    },
  ) => void;
  isRemoveDisabled?: boolean;
}) {
  const { data: persons } = useGetPersons();
  const { data: organizations } = useGetOrganizations();
  const t = useLocale((state) => state.locale);

  return (
    <div className="grid grid-cols-5 gap-2">
      <Select
        label="Type"
        options={[
          { label: "Person", value: "person" },
          { label: "Organization", value: "organization" },
        ]}
        value={"personId" in tenant ? "person" : "organization"}
        onChange={(value) => {
          if (value === "person") {
            onChange({
              personId: "",
            });
          } else {
            onChange({
              organizationId: "",
            });
          }
        }}
      />
      {"personId" in tenant ? (
        <Select
          label={t.person.person}
          options={
            persons
              ?.filter(
                (person) =>
                  !excludeTenants.some(
                    (o) => "personId" in o && o.personId === person.id,
                  ),
              )
              .map((person) => ({
                label: formatPersonLabel(person),
                value: person.id,
              })) ?? []
          }
          placeholder={t.select.placeholder}
          value={tenant.personId}
          onChange={(value) => onChange({ ...tenant, personId: value })}
        />
      ) : "organizationId" in tenant ? (
        <Select
          label={t.organization.organization}
          options={
            organizations
              ?.filter(
                (organization) =>
                  !excludeTenants.some(
                    (o) =>
                      "organizationId" in o &&
                      o.organizationId === organization.id,
                  ),
              )
              ?.map((organization) => ({
                label: formatOrganizationLabel(organization),
                value: organization.id,
              })) ?? []
          }
          placeholder={t.select.placeholder}
          value={tenant.organizationId}
          onChange={(value) => onChange({ ...tenant, organizationId: value })}
        />
      ) : null}
      <Button disabled={isRemoveDisabled} severity="danger" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}
